import { useState, useContext } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Container, Paper, Typography, Box, Grid, Button } from '@mui/material';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFormText,
    SttFormPassword,
    SttFormCheck,
} from '../../../sporttia/all';
import LogoHeading from '../../../layout/LogoHeading';

export default function GameRegister() {
    const { id } = useParams();
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const location = useLocation();

    const query = new URLSearchParams(location.search);
    const emailFromQuery = query.get('email') || '';

    const [form, setForm] = useState({
        name: '',
        surname1: '',
        email: emailFromQuery,
        password: '',
        nivel: '',
    });
    const [privacyChecked, setPrivacyChecked] = useState(false);
    const [olderThan14, setOlderThan14] = useState(false);
    const [loading, setLoading] = useState(false);
    const [, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const handleChange = ({ name, value }: any) => {
        setForm({ ...form, [name]: value });
    };

    const handleRegister = (e: any) => {
        e.preventDefault();
        setError('');
        setSuccessMsg('');

        if (!privacyChecked || !olderThan14) {
            cxt.showMessage('E', t('register.mustAcceptAll'));
            setError(t('register.mustAcceptAll'));
            return;
        }

        if (form.password.length < 6) {
            cxt.showMessage('E', t('register.passwordMoreThanSix'));
            setError(t('register.passwordMoreThanSix'));
            return;
        }

        setLoading(true);

        cxt.api('POST', '/users', {
            params: {
                name: form.name,
                surname1: form.surname1,
                email: form.email,
                password: form.password,
                registerForGame: true,
                level: form.nivel,
                language: cxt?.lang?.key || navigator.language,
            },
            success: (response: any) => {
                setLoading(false);
                if (response.user) {
                    cxt.api('POST', `/games/${id}/signup`, {
                        params: {
                            email: form.email,
                            gameId: id,
                        },
                        success: () => {
                            setSuccessMsg(
                                t('register.emailSentForInscription'),
                            );
                        },
                        error: () => {
                            cxt.showMessage(
                                'E',
                                t('register.errorSendingEmail'),
                            );
                            setError(t('register.errorSendingEmail'));
                        },
                    });
                }
            },
            error: (response: any) => {
                setLoading(false);
                if (response.error) {
                    cxt.showMessage('E', response.error.msg);
                    setError(response.error.msg);
                }
            },
        });
    };

    return (
        <Container maxWidth="sm">
            <Box mt={8}>
                <Paper>
                    <Box p={8}>
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }}>
                                <LogoHeading
                                    caption={t('register.title').toUpperCase()}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <SttFormText
                                    name="name"
                                    caption={t('labels.name')}
                                    defVal={form.name}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <SttFormText
                                    name="surname1"
                                    caption={t('labels.surname1')}
                                    defVal={form.surname1}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <SttFormText
                                    name="email"
                                    caption={t('labels.email')}
                                    defVal={form.email}
                                    onChange={handleChange}
                                    type="email"
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <SttFormPassword
                                    name="password"
                                    caption={t('user.password')}
                                    defVal={form.password}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <SttFormText
                                    name="nivel"
                                    caption={t('form.paddleLevel')}
                                    defVal={form.nivel}
                                    onChange={handleChange}
                                    placeholder={t('form.example', {
                                        value: '3.50',
                                    })}
                                    helperText={t('games.skillScaleInfo')}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <SttFormCheck
                                    caption={
                                        <span>
                                            {t('register.acceptTerms1')}{' '}
                                            <Link
                                                to="/privacy"
                                                className="text-brand-text no-underline hover:underline"
                                            >
                                                {t('register.privacyPolicy')}
                                            </Link>
                                        </span>
                                    }
                                    checked={privacyChecked}
                                    onChange={({ value }: any) =>
                                        setPrivacyChecked(value)
                                    }
                                />
                                <SttFormCheck
                                    caption={t('register.olderThan14')}
                                    checked={olderThan14}
                                    onChange={({ value }: any) =>
                                        setOlderThan14(value)
                                    }
                                />
                                <Box>
                                    <Typography variant="body2">
                                        {t('register.olderThan14Desc')}
                                    </Typography>
                                </Box>
                            </Grid>

                            {successMsg && (
                                <Grid size={{ xs: 12 }}>
                                    <Typography color="primary">
                                        {successMsg}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid size={{ xs: 12 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    startIcon={<HowToRegIcon />}
                                    disabled={loading}
                                    onClick={handleRegister}
                                >
                                    {t('register.register')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
}
